/**
 * TruthProfitabilityReport — Fleet Intelligence Profitability Tab
 *
 * Displays profitability data sourced from the server2 truth layer
 * (/profitability/v2/merged-costs). Unlike the standalone ProfitabilityReport
 * tab which recalculates fuel cost from config assumptions, this component
 * uses the truth layer's observed jigsaw_fuel_cost and server-computed
 * profit/total_cost — respecting the data constitution:
 *
 *   §13.5  Use-case neutrality: same facts for all consumers
 *   §13.7  No silent assumptions
 *   §8.1   Derived values persisted with evidence
 *   §13.8  Weakest contributing completeness propagated
 *
 * Core views: KPI cards, trend chart, daily summary table, data source status.
 */

import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { PoundSterling, AlertTriangle, CheckCircle, RefreshCw, X, ChevronLeft, ChevronRight, Download, Settings } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { Badge, type BadgeTone } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { fetchTruthMergedOrders, fetchTruthCustomerProfitabilityDaily } from '../../services/profitabilityTruthClient';
import type { TruthCustomerDailyRow, TruthMergedOrderRow } from '../../types/profitabilityTruth';
import { useProfitabilityDateRange } from '../../hooks/useProfitabilityDateRange';
import { useTruthProfitabilityPanel } from '../../hooks/useTruthProfitabilityPanel';
import { CHART_PALETTE } from '../../config/colors';
import { deduplicateOrders, type OrderDedupAccessors } from '../../utils/orderDedup';
import ProfitabilityDateRangePicker from '../profitability/ProfitabilityDateRangePicker';
import TruthProfitabilityPipelinePanel from '../profitability/TruthProfitabilityPipelinePanel';
import ProfitabilityAssumptionsPanel from '../profitability/ProfitabilityAssumptionsPanel';
import LineChart from '../LineChart';

// ============================================================================
// Types
// ============================================================================

/** Per-order revenue breakdown item from truth layer computation */
interface RevenueBreakdownItem {
  order_entity_id: string;
  order_name?: string;
  revenue: number;
  imputed?: boolean;
}

/** Merged cost record from truth layer — same 10 columns as server1 + derived fields */
interface TruthMergedCostRow {
  date: string;
  vehicle: string;
  daily_miles: number;
  driving_hours: number;
  waiting_hours: number;
  driver_cost: number;
  jigsaw_fuel_cost: number;
  jigsaw_fuel_quantity: number;
  jigsaw_fuel_unitPrice: number;
  revenue: number;
  order_count?: number;
  revenue_breakdown?: RevenueBreakdownItem[];
  vehicle_type?: string;
  depreciation?: number;
  /** Truth-layer-computed: fuel + driver cost */
  total_cost: number;
  /** Truth-layer-computed: revenue - total_cost */
  profit: number;
  /** Truth-layer-computed: (profit / revenue) × 100 */
  profit_margin: number;
}

/** Full record with provenance (from /merged-costs/full) */
interface ProvenanceInfo {
  completeness: 'Observed' | 'Inferred' | 'Estimated' | 'Assumed' | 'Unknown';
  source_event_ids: string[];
  source_edge_ids?: string[];
  fallback_method?: string;
}

interface TruthMergedCostRowFull extends TruthMergedCostRow {
  vehicle_entity_id: string;
  organisation_id: string;
  provenance: {
    daily_miles: ProvenanceInfo;
    driving_hours: ProvenanceInfo;
    waiting_hours: ProvenanceInfo;
    driver_cost: ProvenanceInfo;
    jigsaw_fuel_cost: ProvenanceInfo;
    revenue: ProvenanceInfo;
    total_cost: ProvenanceInfo;
    profit: ProvenanceInfo;
  };
  computed_at: string;
}

/** Aggregated daily summary row (grouped by period + vehicle) */
interface DailySummaryRow {
  date: string;
  vehicleId: string;
  totalMileage: number;
  totalRevenue: number;
  totalFuelCost: number;
  totalDriverCost: number;
  totalProfit: number;
  totalDrivingHours: number;
  totalWaitingHours: number;
  currency: string;
  periodLabel: string;
  /** Per-order revenue breakdown (aggregated across rows in this group) */
  revenueBreakdown: RevenueBreakdownItem[];
  orderCount: number;
  /** Vehicle type from truth layer */
  vehicleType: string;
  /** Jigsaw fuel quantity (litres) */
  totalFuelQuantity: number;
  /** Jigsaw fuel unit price */
  fuelUnitPrice: number;
  /** Depreciation cost */
  totalDepreciation: number;
}

type SortField = 'date' | 'vehicleId' | 'totalMileage' | 'totalRevenue' | 'totalFuelCost' | 'totalDriverCost' | 'totalProfit';
type PeriodType = 'day' | 'week' | 'month';

// ============================================================================
// Helpers
// ============================================================================

/** Get ISO week start/end for a date */
function getWeekRange(dateStr: string): { startDate: string; endDate: string; label: string } {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const startDate = monday.toISOString().slice(0, 10);
  const endDate = sunday.toISOString().slice(0, 10);
  const label = `${startDate} – ${endDate}`;
  return { startDate, endDate, label };
}

function formatMonthLabel(ym: string): string {
  const [year, month] = ym.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(month) - 1]} ${year.slice(-2)}`;
}

/** Provenance badge: Observed/Inferred are provenance tiers (shared colors
 *  module via `Badge`'s `provenance` prop); the rest take the nearest tone. */
function completenessBadge(level: string): { tone: BadgeTone; provenance?: 'observed' | 'inferred' } {
  switch (level) {
    case 'Observed': return { tone: 'neutral', provenance: 'observed' };
    case 'Inferred': return { tone: 'neutral', provenance: 'inferred' };
    case 'Estimated':
    case 'Assumed': return { tone: 'warning' };
    default: return { tone: 'neutral' };
  }
}

// ============================================================================
// Component
// ============================================================================

export default function TruthProfitabilityReport() {
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    resetToDefault,
  } = useProfitabilityDateRange();
  const [periodType, setPeriodType] = useState<PeriodType>('day');

  // Data state
  const [records, setRecords] = useState<TruthMergedCostRow[]>([]);
  const [fullRecords, setFullRecords] = useState<TruthMergedCostRowFull[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchDataRef = useRef<() => void>(() => {});
  const [error, setError] = useState<string | null>(null);
  const [showProvenance, setShowProvenance] = useState(false);

  // Table state
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
  const [chartHighlightSeries, setChartHighlightSeries] = useState<string | null>(null);

  // ── Active view tab ────────────────────────────────────────────────────

  type ViewTab = 'summary' | 'orders' | 'customers';
  const [activeTab, setActiveTab] = useState<ViewTab>('summary');

  // ── Merged Orders State ────────────────────────────────────────────────

  interface MergedOrderRow {
    date: string;
    order_name?: string;
    order_entity_id?: string;
    customer_name?: string;
    origin?: string;
    destination?: string;
    service_level?: string;
    goods_quantity?: number;
    goods_weight?: number;
    vehicle_assigned?: string;
    vehicle_pickup?: string;
    vehicle_observed?: string;
    revenue?: number;
    revenue_imputed?: boolean;
    [key: string]: unknown;
  }

  const [ordersData, setOrdersData] = useState<MergedOrderRow[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);

  // ── Customer daily profitability state ──────────────────────────────────

  interface CustomerDailyRow {
    period: string;
    customer_name: string;
    order_count: number;
    revenue: number;
    allocated_cost: number;
    profit: number;
    profit_margin: number;
    cost_per_mile?: number;
    [key: string]: unknown;
  }

  const [customersData, setCustomersData] = useState<CustomerDailyRow[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  // ── Historical Import State ─────────────────────────────────────────────

  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<{ status: string; artifactsImported: number; errors: string[] } | null>(null);

  const [showConfigPanel, setShowConfigPanel] = useState(false);

  // ── Data Fetching ──────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const mergedRes = await authenticatedFetch(showProvenance
        ? API_ENDPOINTS.profitabilityV2.mergedCostsFull(startDate, endDate)
        : API_ENDPOINTS.profitabilityV2.mergedCosts(startDate, endDate)
      );

      if (!mergedRes.ok) {
        throw new Error(`Failed to load profitability data: ${mergedRes.status} ${mergedRes.statusText}`);
      }

      const mergedJson = await mergedRes.json();
      const fetchedRecords = Array.isArray(mergedJson.records) ? mergedJson.records : [];

      if (showProvenance) {
        setFullRecords(fetchedRecords);
        setRecords(fetchedRecords.map((r: TruthMergedCostRowFull) => ({
          date: r.date,
          vehicle: r.vehicle,
          daily_miles: r.daily_miles,
          driving_hours: r.driving_hours,
          waiting_hours: r.waiting_hours,
          driver_cost: r.driver_cost,
          jigsaw_fuel_cost: r.jigsaw_fuel_cost,
          jigsaw_fuel_quantity: r.jigsaw_fuel_quantity,
          jigsaw_fuel_unitPrice: r.jigsaw_fuel_unitPrice,
          revenue: r.revenue,
          total_cost: r.total_cost,
          profit: r.profit,
          profit_margin: r.profit_margin,
        })));
      } else {
        setRecords(fetchedRecords);
        setFullRecords([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, showProvenance]);

  fetchDataRef.current = () => { void fetchData(); };

  const truthPipeline = useTruthProfitabilityPanel({
    startDate,
    endDate,
    onDataRefresh: () => fetchDataRef.current(),
    // On-demand coverage can OOM zeecore; prefer manifest coverage_summary.
    includeCoverage: false,
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [periodType, selectedVehicles, sortField, sortAsc]);

  const mergedOrderRowAccessors: OrderDedupAccessors<MergedOrderRow> = {
    orderId: (r) => r.order_name ?? '',
    customer: (r) => r.customer_name,
    date: (r) => r.date,
    origin: (r) => r.origin,
    destination: (r) => r.destination,
    revenue: (r) => r.revenue,
    vehicleAssigned: (r) => r.vehicle_assigned,
    vehicleObserved: (r) => r.vehicle_observed,
  };

  function deduplicateMergedOrderRows(rows: MergedOrderRow[]): MergedOrderRow[] {
    return deduplicateOrders(rows, mergedOrderRowAccessors);
  }

  function mapTruthMergedOrders(rows: TruthMergedOrderRow[]): MergedOrderRow[] {
    const mapped = rows.map((row) => {
      const date =
        (row.destination_end_timestamp_local || row.origin_end_timestamp_local || '').trim().slice(0, 10);
      const origin = [row.origin_city, row.origin_postal_code].filter(Boolean).join(', ');
      const destination = [row.destination_city, row.destination_postal_code].filter(Boolean).join(', ');
      const revenue = Number(row.total_revenue_tenant_currency) || 0;
      const goodsQty = row.goods_quantity ? Number(row.goods_quantity) : undefined;
      const goodsWeight = row.goods_weight ? Number(row.goods_weight) : undefined;

      return {
        date,
        order_name: row.order_id,
        customer_name: row.customer,
        origin: origin || undefined,
        destination: destination || undefined,
        service_level: row.service_level_name,
        goods_quantity: Number.isFinite(goodsQty) ? goodsQty : undefined,
        goods_weight: Number.isFinite(goodsWeight) ? goodsWeight : undefined,
        vehicle_assigned: row.vehicle_assigned,
        vehicle_pickup: row.vehicle_pickup,
        vehicle_observed: row.vehicle_observed,
      revenue,
      revenue_imputed: row.revenue_imputed === 1,
      };
    });
    return deduplicateMergedOrderRows(mapped);
  }

  function mapTruthCustomerDailyRows(rows: TruthCustomerDailyRow[]): CustomerDailyRow[] {
    return rows.map((row) => ({
      period: row.period,
      customer_name: row.customer_name,
      order_count: Number(row.order_count) || 0,
      revenue: Number(row.total_revenue_gbp) || 0,
      allocated_cost: Number(row.total_allocated_cost_gbp) || 0,
      profit: Number(row.profit_gbp) || 0,
      profit_margin: Number(row.profit_margin_pct) || 0,
      cost_per_mile: Number(row.cost_per_mile_gbp) || 0,
    }));
  }

  // ── Merged Orders Fetching ────────────────────────────────────────────

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const { rows } = await fetchTruthMergedOrders(startDate, endDate);
      setOrdersData(mapTruthMergedOrders(rows));
    } catch { /* non-blocking */ }
    setOrdersLoading(false);
  }, [startDate, endDate]);

  useEffect(() => {
    if (activeTab === 'orders') fetchOrders();
  }, [activeTab, fetchOrders]);

  // ── Customer daily fetching ───────────────────────────────────────────

  const fetchCustomers = useCallback(async () => {
    setCustomersLoading(true);
    try {
      const { rows } = await fetchTruthCustomerProfitabilityDaily(startDate, endDate);
      setCustomersData(mapTruthCustomerDailyRows(rows));
    } catch { /* non-blocking */ }
    setCustomersLoading(false);
  }, [startDate, endDate]);

  useEffect(() => {
    if (activeTab === 'customers') fetchCustomers();
  }, [activeTab, fetchCustomers]);

  // ── Historical Import ─────────────────────────────────────────────────

  const doHistoricalImport = useCallback(async () => {
    setImportLoading(true);
    setImportResult(null);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.profitabilityV2.importHistorical(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startDate, endDate, triggerCompute: true }),
      });
      if (res.ok) {
        const data = await res.json();
        setImportResult({ status: data.status, artifactsImported: data.artifactsImported ?? 0, errors: data.errors ?? [] });
        // Reload data after import
        fetchData();
      } else {
        setImportResult({ status: 'failed', artifactsImported: 0, errors: ['Import request failed'] });
      }
    } catch {
      setImportResult({ status: 'failed', artifactsImported: 0, errors: ['Import request failed'] });
    }
    setImportLoading(false);
  }, [startDate, endDate, fetchData]);

  // ── Data Processing ────────────────────────────────────────────────────

  /** Group records by period (day/week/month) + vehicle */
  const dailySummaryData = useMemo(() => {
    const grouped = new Map<string, DailySummaryRow>();

    for (const row of records) {
      const dateKey = (row.date || '').slice(0, 10);
      const vid = row.vehicle || '';
      if (!dateKey || !vid) continue;

      let periodKey: string;
      let periodLabel: string;

      if (periodType === 'day') {
        periodKey = `${dateKey}|${vid}`;
        periodLabel = dateKey;
      } else if (periodType === 'week') {
        const week = getWeekRange(dateKey);
        periodKey = `${week.startDate}|${week.endDate}|${vid}`;
        periodLabel = week.label;
      } else {
        const ym = dateKey.substring(0, 7);
        periodKey = `${ym}|${vid}`;
        periodLabel = formatMonthLabel(ym);
      }

      const existing = grouped.get(periodKey);
      // Use truth-layer values directly (no frontend recalculation)
      const fuelCost = row.jigsaw_fuel_cost ?? 0;
      const driverCost = row.driver_cost ?? 0;
      const revenue = row.revenue ?? 0;
      const profit = row.profit ?? (revenue - fuelCost - driverCost);
      const miles = row.daily_miles ?? 0;

      if (existing) {
        existing.totalMileage += miles;
        existing.totalRevenue += revenue;
        existing.totalFuelCost += fuelCost;
        existing.totalDriverCost += driverCost;
        existing.totalProfit += profit;
        existing.totalDrivingHours += row.driving_hours ?? 0;
        existing.totalWaitingHours += row.waiting_hours ?? 0;
        existing.revenueBreakdown.push(...(row.revenue_breakdown ?? []));
        existing.orderCount += row.order_count ?? 0;
        existing.totalFuelQuantity += row.jigsaw_fuel_quantity ?? 0;
        existing.totalDepreciation += row.depreciation ?? 0;
      } else {
        grouped.set(periodKey, {
          date: dateKey,
          vehicleId: vid,
          totalMileage: miles,
          totalRevenue: revenue,
          totalFuelCost: fuelCost,
          totalDriverCost: driverCost,
          totalProfit: profit,
          totalDrivingHours: row.driving_hours ?? 0,
          totalWaitingHours: row.waiting_hours ?? 0,
          currency: 'GBP',
          periodLabel,
          revenueBreakdown: [...(row.revenue_breakdown ?? [])],
          orderCount: row.order_count ?? 0,
          vehicleType: row.vehicle_type ?? 'Default',
          totalFuelQuantity: row.jigsaw_fuel_quantity ?? 0,
          fuelUnitPrice: row.jigsaw_fuel_unitPrice ?? 0,
          totalDepreciation: row.depreciation ?? 0,
        });
      }
    }

    let arr = Array.from(grouped.values());

    // Apply vehicle filter
    if (selectedVehicles.length > 0) {
      arr = arr.filter(r => selectedVehicles.includes(r.vehicleId));
    }

    // Only show rows with some activity
    arr = arr.filter(r => r.totalRevenue > 0 || r.totalFuelCost > 0 || r.totalDriverCost > 0 || r.totalMileage > 0);

    // Sort
    arr.sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      switch (sortField) {
        case 'date': aVal = a.date; bVal = b.date; break;
        case 'vehicleId': aVal = a.vehicleId; bVal = b.vehicleId; break;
        case 'totalMileage': aVal = a.totalMileage; bVal = b.totalMileage; break;
        case 'totalRevenue': aVal = a.totalRevenue; bVal = b.totalRevenue; break;
        case 'totalFuelCost': aVal = a.totalFuelCost; bVal = b.totalFuelCost; break;
        case 'totalDriverCost': aVal = a.totalDriverCost; bVal = b.totalDriverCost; break;
        case 'totalProfit': aVal = a.totalProfit; bVal = b.totalProfit; break;
      }
      if (aVal < bVal) return sortAsc ? -1 : 1;
      if (aVal > bVal) return sortAsc ? 1 : -1;
      return 0;
    });

    return arr;
  }, [records, periodType, selectedVehicles, sortField, sortAsc]);

  // Pagination
  const totalPages = Math.ceil(dailySummaryData.length / rowsPerPage);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return dailySummaryData.slice(start, start + rowsPerPage);
  }, [dailySummaryData, page, rowsPerPage]);

  // ── KPI Totals ─────────────────────────────────────────────────────────

  const kpiTotals = useMemo(() => {
    return dailySummaryData.reduce(
      (acc, row) => ({
        totalRevenue: acc.totalRevenue + row.totalRevenue,
        totalFuelCost: acc.totalFuelCost + row.totalFuelCost,
        totalDriverCost: acc.totalDriverCost + row.totalDriverCost,
        totalProfit: acc.totalProfit + row.totalProfit,
        totalMileage: acc.totalMileage + row.totalMileage,
      }),
      { totalRevenue: 0, totalFuelCost: 0, totalDriverCost: 0, totalProfit: 0, totalMileage: 0 },
    );
  }, [dailySummaryData]);

  // ── Vehicle entity ID map (vehicle name → truth-layer entity ID) ─────────

  const vehicleEntityIdMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of fullRecords) {
      if (r.vehicle && r.vehicle_entity_id) {
        map.set(r.vehicle, r.vehicle_entity_id);
      }
    }
    return map;
  }, [fullRecords]);

  // ── Overall provenance summary ─────────────────────────────────────────

  const provenanceSummary = useMemo(() => {
    if (fullRecords.length === 0) return null;
    const counts: Record<string, number> = { Observed: 0, Inferred: 0, Estimated: 0, Assumed: 0, Unknown: 0 };
    for (const r of fullRecords) {
      if (r.provenance?.profit?.completeness) {
        counts[r.provenance.profit.completeness] = (counts[r.provenance.profit.completeness] || 0) + 1;
      }
    }
    return counts;
  }, [fullRecords]);

  // ── Chart Series ───────────────────────────────────────────────────────

  const chartSeries = useMemo(() => {
    const byPeriod = new Map<string, { date: string; revenue: number; costs: number; profit: number }>();

    for (const row of dailySummaryData) {
      const key = row.periodLabel || row.date;
      const existing = byPeriod.get(key);
      if (existing) {
        existing.revenue += row.totalRevenue;
        existing.costs += row.totalFuelCost + row.totalDriverCost;
        existing.profit += row.totalProfit;
        if (row.date < existing.date) existing.date = row.date;
      } else {
        byPeriod.set(key, {
          date: row.date,
          revenue: row.totalRevenue,
          costs: row.totalFuelCost + row.totalDriverCost,
          profit: row.totalProfit,
        });
      }
    }

    const sorted = Array.from(byPeriod.entries()).sort((a, b) => a[1].date.localeCompare(b[1].date));

    return [
      { name: 'Revenue', data: sorted.map(([label, agg]) => ({ label, value: agg.revenue })), color: CHART_PALETTE[4], strokeWidth: 1 },
      { name: 'Total Costs', data: sorted.map(([label, agg]) => ({ label, value: agg.costs })), color: CHART_PALETTE[1], strokeWidth: 1 },
      { name: 'Net Profit', data: sorted.map(([label, agg]) => ({ label, value: agg.profit })), color: CHART_PALETTE[2], strokeWidth: 4 },
    ];
  }, [dailySummaryData]);

  // ── Unique vehicles for filter ─────────────────────────────────────────

  const allVehicles = useMemo(() => {
    return [...new Set(records.map(r => r.vehicle))].sort();
  }, [records]);

  // ── Sort handler ───────────────────────────────────────────────────────

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  const toggleVehicle = (vid: string) => {
    setSelectedVehicles(prev =>
      prev.includes(vid) ? prev.filter(v => v !== vid) : [...prev, vid]
    );
  };

  const handleExportCSV = () => {
    if (dailySummaryData.length === 0) return;
    const periodLabel = periodType === 'day' ? 'Date' : periodType === 'week' ? 'Week' : 'Month';
    const headers = [periodLabel, 'Vehicle', 'Miles', 'Revenue', 'Fuel Cost', 'Driver Cost', 'Profit', 'Margin %', 'Driving Hours', 'Waiting Hours', 'Currency'];
    const escapeCell = (val: string | number) => {
      const s = String(val ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = dailySummaryData.map(row => {
      const margin = row.totalRevenue > 0 ? ((row.totalProfit / row.totalRevenue) * 100).toFixed(1) : '0.0';
      return [
        escapeCell(row.periodLabel || row.date),
        escapeCell(row.vehicleId),
        escapeCell(row.totalMileage?.toFixed(1) ?? '0'),
        escapeCell(row.totalRevenue?.toFixed(2) ?? '0'),
        escapeCell(row.totalFuelCost?.toFixed(2) ?? '0'),
        escapeCell(row.totalDriverCost?.toFixed(2) ?? '0'),
        escapeCell(row.totalProfit?.toFixed(2) ?? '0'),
        escapeCell(margin),
        escapeCell(row.totalDrivingHours?.toFixed(2) ?? '0'),
        escapeCell(row.totalWaitingHours?.toFixed(2) ?? '0'),
        escapeCell(row.currency ?? 'GBP'),
      ].join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const dateStamp = new Date().toISOString().slice(0, 10);
    a.download = `truth_layer_profitability_${startDate}_to_${endDate}_${dateStamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <PoundSterling className="w-5 h-5 text-success" />
            <div>
              <h3 className="text-sm font-semibold text-white">Truth Layer Profitability</h3>
              <p className="text-xs text-muted-foreground">
                Data from canonical events — no silent assumptions
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <ProfitabilityDateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onReset={resetToDefault}
            />

            {/* Period selector */}
            <div className="flex items-center bg-secondary rounded-md p-0.5">
              {(['day', 'week', 'month'] as PeriodType[]).map(p => (
                <Button
                  key={p}
                  variant={periodType === p ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPeriodType(p)}
                  className={cn(
                    'h-auto rounded px-2.5 py-1 text-xs font-medium',
                    periodType === p ? 'shadow' : 'text-foreground hover:bg-transparent hover:text-foreground',
                  )}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </Button>
              ))}
            </div>

            {/* Provenance toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowProvenance(!showProvenance)}
              className={cn(
                'h-auto rounded border px-3 py-1.5 text-xs font-medium',
                showProvenance
                  ? 'border-ring/50 bg-ring/20 text-ring hover:bg-ring/20'
                  : 'border-accent bg-secondary text-muted-foreground hover:text-foreground',
              )}
            >
              Provenance
            </Button>

            {/* Settings panel toggle */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowConfigPanel((v) => !v)}
              className={cn(
                'h-auto gap-1.5 rounded border px-3 py-1.5 text-xs font-medium',
                showConfigPanel
                  ? 'border-warning/50 bg-warning/20 text-warning hover:bg-warning/20'
                  : 'border-accent bg-secondary text-foreground hover:border-warning hover:text-foreground',
              )}
              title="Profitability assumptions"
            >
              <Settings className="w-3.5 h-3.5 text-warning" />
              Settings
            </Button>

            {/* Clear filters */}
            {selectedVehicles.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedVehicles([])}
                className="h-auto gap-1 rounded border border-accent px-3 py-1.5 text-xs font-medium"
              >
                <X className="w-3 h-3" />
                Clear ({selectedVehicles.length})
              </Button>
            )}

            {/* Historical Import */}
            <Button
              variant="default"
              size="sm"
              onClick={doHistoricalImport}
              disabled={importLoading}
              className="h-auto gap-1.5 rounded bg-chart-5 px-3 py-1.5 text-xs font-medium text-white hover:bg-chart-5/90 disabled:bg-accent"
              title="Import legacy profitability data into truth layer"
            >
              <Download className={`w-3.5 h-3.5 ${importLoading ? 'animate-pulse' : ''}`} />
              {importLoading ? 'Importing…' : 'Import Legacy'}
            </Button>
          </div>
        </div>

        {/* View tab selector */}
        <div className="flex items-center gap-1 mt-3 border-t border-border pt-3">
          {([
            { key: 'summary' as ViewTab, label: 'Daily Summary' },
            { key: 'orders' as ViewTab, label: 'Orders' },
            { key: 'customers' as ViewTab, label: 'Customer Profitability' },
          ]).map(tab => (
            <Button
              key={tab.key}
              variant={activeTab === tab.key ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'h-auto rounded px-3 py-1.5 text-xs font-medium',
                activeTab === tab.key ? 'shadow' : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
              )}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Import result banner */}
        {importResult && (
          <div className={`flex items-center gap-2 mt-2 px-3 py-2 rounded text-xs ${importResult.status === 'completed'
              ? 'bg-success/20 border border-success/40 text-success'
              : importResult.status === 'completed_with_errors'
                ? 'bg-warning/20 border border-warning/40 text-warning'
                : 'bg-destructive/20 border border-destructive/40 text-destructive'
            }`}>
            {importResult.status === 'completed' ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            <span>Import {importResult.status}: {importResult.artifactsImported} artifacts imported</span>
            {importResult.errors.length > 0 && <span>({importResult.errors.length} errors)</span>}
            <Button variant="ghost" size="sm" onClick={() => setImportResult(null)} className="ml-auto h-auto px-0 text-muted-foreground hover:bg-transparent hover:text-foreground"><X className="w-3 h-3" /></Button>
          </div>
        )}
      </div>

      <TruthProfitabilityPipelinePanel panel={truthPipeline} />

      <ProfitabilityAssumptionsPanel
        visible={showConfigPanel}
        onClose={() => setShowConfigPanel(false)}
      />

      {/* Provenance Summary */}
      {showProvenance && provenanceSummary && (
        <div className="bg-card/50 border border-ring/20 rounded-lg p-3">
          <div className="flex items-center gap-4">
            <span className="text-xs font-medium text-ring uppercase tracking-wide">Profit Provenance:</span>
            {Object.entries(provenanceSummary).filter(([, count]) => count > 0).map(([level, count]) => (
              <Badge key={level} {...completenessBadge(level)}>
                {level}: {count}
              </Badge>
            ))}
            <span className="text-xs text-muted-foreground ml-auto">
              {fullRecords.length} vehicle-day records with full provenance
            </span>
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="bg-destructive/20 border border-destructive/30 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-destructive" />
          <span className="text-sm text-destructive">{error}</span>
          <Button variant="link" size="sm" onClick={fetchData} className="ml-auto h-auto px-0 text-xs text-destructive hover:text-destructive">Retry</Button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 text-success animate-spin" />
          <span className="ml-3 text-muted-foreground">Loading profitability data from truth layer...</span>
        </div>
      )}

      {!loading && !error && records.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-12 flex flex-col items-center gap-4">
          <PoundSterling className="w-12 h-12 text-accent" />
          <div className="text-center">
            <p className="text-foreground font-medium">No profitability data available</p>
            <p className="text-sm text-muted-foreground mt-1">
              Click <strong>Compute</strong> to generate projections from truth layer events for the selected date range
            </p>
          </div>
        </div>
      )}

      {!loading && records.length > 0 && activeTab === 'summary' && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Profit Margin % */}
            <div
              className="relative overflow-hidden bg-gradient-to-br from-chart-10/20 via-card to-background rounded-xl border border-chart-10/30 p-6 shadow-xl hover:shadow-chart-10/20 transition-all duration-500 transform hover:scale-105 cursor-pointer"
              onMouseEnter={() => setChartHighlightSeries(null)}
              onMouseLeave={() => setChartHighlightSeries(null)}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-chart-10/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-chart-10 rounded-full animate-pulse"></div>
                  <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Profit Margin %</div>
                </div>
                <div className="text-4xl font-bold text-white mb-1">
                  {kpiTotals.totalRevenue > 0
                    ? `${((kpiTotals.totalProfit / kpiTotals.totalRevenue) * 100).toFixed(1)}%`
                    : '—'}
                </div>
                <div className="text-xs text-muted-foreground font-medium">(Profit / Revenue) × 100</div>
              </div>
            </div>

            {/* Net Profit */}
            <div
              className={`relative overflow-hidden bg-gradient-to-br ${kpiTotals.totalProfit >= 0
                  ? 'from-success/20 via-card to-background border-success/30 hover:shadow-success/20'
                  : 'from-destructive/20 via-card to-background border-destructive/30 hover:shadow-destructive/20'
                } rounded-xl border p-6 shadow-xl transition-all duration-500 transform hover:scale-105 cursor-pointer`}
              onMouseEnter={() => setChartHighlightSeries('Net Profit')}
              onMouseLeave={() => setChartHighlightSeries(null)}
            >
              <div className={`absolute top-0 right-0 w-32 h-32 ${kpiTotals.totalProfit >= 0 ? 'bg-success/10' : 'bg-destructive/10'} rounded-full -mr-16 -mt-16 blur-2xl`}></div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 ${kpiTotals.totalProfit >= 0 ? 'bg-success' : 'bg-destructive'} rounded-full animate-pulse`}></div>
                  <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Net Profit</div>
                </div>
                <div className="text-4xl font-bold text-white mb-1">
                  {kpiTotals.totalProfit.toLocaleString(undefined, { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                </div>
                <div className="text-xs text-muted-foreground font-medium">Revenue minus total costs (truth layer)</div>
              </div>
            </div>

            {/* Revenue — wrapper has no overflow-hidden so tooltip can show above */}
            <div
              className="group relative"
              onMouseEnter={() => setChartHighlightSeries('Revenue')}
              onMouseLeave={() => setChartHighlightSeries(null)}
            >
              <div className="overflow-hidden bg-gradient-to-br from-chart-5/20 via-card to-background rounded-xl border border-chart-5/30 p-6 shadow-xl hover:shadow-chart-5/20 transition-all duration-500 transform hover:scale-105 cursor-pointer">
                <div className="absolute top-0 right-0 w-32 h-32 bg-chart-5/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-chart-5 rounded-full animate-pulse"></div>
                    <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Revenue</div>
                  </div>
                  <div className="text-4xl font-bold text-white mb-1">
                    {kpiTotals.totalRevenue.toLocaleString(undefined, { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                  </div>
                  <div className="text-xs text-muted-foreground font-medium">Sum from truth layer orders</div>
                </div>
              </div>
              {/* Hover tooltip */}
              {dailySummaryData.length > 0 && (
                <div className="absolute bottom-full right-0 mb-2 translate-x-20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 flex justify-end">
                  <div className="px-4 py-3 bg-popover border border-chart-5/50 rounded-lg shadow-xl text-left text-sm text-foreground min-w-50">
                    <div className="font-semibold text-chart-5 mb-2 pb-1 border-b border-accent text-center">Revenue breakdown</div>
                    <div className="space-y-1">
                      <div>
                        <span className="text-muted-foreground">Total orders:</span>{' '}
                        <span className="text-white font-medium">
                          {dailySummaryData.reduce((sum, r) => sum + r.orderCount, 0)}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total revenue:</span>{' '}
                        <span className="text-white font-medium">
                          {kpiTotals.totalRevenue.toLocaleString(undefined, { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Source:</span>{' '}
                        <span className="text-foreground">Truth layer OrderObserved events</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Total Costs — wrapper has no overflow-hidden so tooltip can show above */}
            <div
              className="group relative"
              onMouseEnter={() => setChartHighlightSeries('Total Costs')}
              onMouseLeave={() => setChartHighlightSeries(null)}
            >
              <div className="overflow-hidden bg-gradient-to-br from-chart-2/20 via-card to-background rounded-xl border border-chart-2/30 p-6 shadow-xl hover:shadow-chart-2/20 transition-all duration-500 transform hover:scale-105 cursor-pointer">
                <div className="absolute top-0 right-0 w-32 h-32 bg-chart-2/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-chart-2 rounded-full animate-pulse"></div>
                    <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Total Costs</div>
                  </div>
                  <div className="text-4xl font-bold text-white mb-1">
                    {(kpiTotals.totalFuelCost + kpiTotals.totalDriverCost).toLocaleString(undefined, { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                  </div>
                  <div className="text-xs text-muted-foreground font-medium">Observed fuel + estimated driver</div>
                </div>
              </div>
              {/* Hover tooltip: breakdown above card */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                <div className="px-4 py-3 bg-popover border border-chart-2/50 rounded-lg shadow-xl text-left text-sm text-foreground min-w-50">
                  <div className="font-semibold text-chart-2 mb-2 pb-1 border-b border-accent text-center">Total Costs breakdown</div>
                  <div className="space-y-1">
                    <div>
                      <span className="text-muted-foreground">Fuel Cost:</span>{' '}
                      <span className="text-white font-medium">
                        {kpiTotals.totalFuelCost.toLocaleString(undefined, { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Driver Cost:</span>{' '}
                      <span className="text-white font-medium">
                        {kpiTotals.totalDriverCost.toLocaleString(undefined, { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trend Chart */}
          {chartSeries.length > 0 && chartSeries[0].data.length > 0 ? (
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground">Profitability Trend</h3>
                <div className="text-xs text-muted-foreground">
                  {dailySummaryData.length} data points • {allVehicles.length} vehicles
                </div>
              </div>
              <div className="h-80">
                <LineChart
                  series={chartSeries}
                  yAxisLabel="Amount (£)"
                  formatYAxisAsK
                  xAxisLabel={periodType}
                  heightClass="h-full min-h-80"
                  chartHeight={265}
                  defaultZoomed
                  highlightedSeriesName={chartHighlightSeries}
                  formatXLabel={(label: string) => {
                    if (periodType === 'day' && label.length >= 10) {
                      const d = new Date(label + 'T12:00:00');
                      return `${d.toLocaleString('en-GB', { month: 'short' })} ${d.getDate()}`;
                    }
                    return label;
                  }}
                  xAxisLabelInterval={periodType === 'day' ? 7 : undefined}
                  hideDataPoints
                  gradientFillSeriesName="Net Profit"
                  highlightLatestPointSeriesName="Net Profit"
                />
              </div>
            </div>
          ) : (
            <div className="bg-card rounded-lg border border-border p-6 flex items-center justify-center min-h-80">
              <p className="text-muted-foreground">No trend data for selected period. Adjust filters.</p>
            </div>
          )}

          {/* Daily Summary Table */}
          <section className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-ring flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  {periodType === 'day' ? 'Daily' : periodType === 'week' ? 'Weekly' : 'Monthly'} Summary by Vehicle
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Truth layer data • {dailySummaryData.length} {dailySummaryData.length === 1 ? 'entry' : 'entries'}
                  {selectedVehicles.length > 0 && ` • ${selectedVehicles.length} vehicle(s) selected`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Vehicle filter dropdown */}
                {allVehicles.length > 0 && (
                  <select
                    value=""
                    onChange={e => { if (e.target.value) toggleVehicle(e.target.value); }}
                    className="bg-popover border border-accent rounded-md px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-success"
                  >
                    <option value="">Filter by vehicle...</option>
                    {allVehicles.map(v => (
                      <option key={v} value={v}>
                        {selectedVehicles.includes(v) ? '✓ ' : ''}{v}
                      </option>
                    ))}
                  </select>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleExportCSV}
                  disabled={dailySummaryData.length === 0}
                  className="border border-accent gap-1.5 disabled:opacity-40"
                  title="Export visible data to CSV"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export CSV
                </Button>
              </div>
            </div>

            {dailySummaryData.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">
                No data available for the selected filters
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/30">
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="px-2 py-3 w-8">{/* checkbox */}</th>
                      <SortableHeader field="date" label={periodType === 'day' ? 'Date' : periodType === 'week' ? 'Week' : 'Month'} current={sortField} asc={sortAsc} onSort={handleSort} />
                      <SortableHeader field="vehicleId" label="Vehicle" current={sortField} asc={sortAsc} onSort={handleSort} />
                      <SortableHeader field="totalMileage" label="Miles" current={sortField} asc={sortAsc} onSort={handleSort} align="right" />
                      <SortableHeader field="totalRevenue" label="Revenue" current={sortField} asc={sortAsc} onSort={handleSort} align="right" className="bg-chart-5 text-white hover:bg-chart-5" />
                      <SortableHeader field="totalFuelCost" label="Fuel Cost" current={sortField} asc={sortAsc} onSort={handleSort} align="right" className="bg-chart-2 text-white hover:bg-chart-2" />
                      <SortableHeader field="totalDriverCost" label="Driver Cost" current={sortField} asc={sortAsc} onSort={handleSort} align="right" className="bg-chart-7 text-white hover:bg-chart-7" />
                      <SortableHeader field="totalProfit" label="Profit" current={sortField} asc={sortAsc} onSort={handleSort} align="right" />
                      {showProvenance && <th className="px-3 py-3 text-center">Provenance</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {paginatedData.map(row => {
                      const isSelected = selectedVehicles.includes(row.vehicleId);
                      // Find provenance for first matching full record
                      const fullMatch = showProvenance
                        ? fullRecords.find(fr => fr.vehicle === row.vehicleId && fr.date.startsWith(row.date))
                        : null;

                      return (
                        <tr key={`${row.date}-${row.vehicleId}`} className="hover:bg-secondary/30 transition-colors">
                          <td className="px-2 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleVehicle(row.vehicleId)}
                              className="w-4 h-4 text-primary bg-secondary border-accent rounded focus:ring-success cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-3 text-foreground">{row.periodLabel || row.date}</td>
                          <td className="px-4 py-3 font-mono text-xs">
                            <Button
                              variant="ghost"
                              className="h-auto px-0 text-xs text-ring cursor-pointer hover:bg-transparent hover:text-ring hover:underline"
                              title="View vehicle in Truth Browser"
                              onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-truth-browser', { detail: { vehicleId: row.vehicleId, vehicleEntityId: vehicleEntityIdMap.get(row.vehicleId) } }))}
                            >
                              {row.vehicleId}
                            </Button>
                          </td>
                          <td className="px-4 py-3 text-right text-foreground font-mono tabular-nums">
                            {row.totalMileage > 0 ? row.totalMileage.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
                          </td>
                          <td
                            className="px-4 py-3 text-right text-white cursor-help group relative overflow-visible"
                            onMouseEnter={(e) => {
                              const tooltip = e.currentTarget.querySelector('.revenue-tooltip') as HTMLElement;
                              if (tooltip) {
                                const rect = e.currentTarget.getBoundingClientRect();
                                tooltip.style.left = `${rect.left + rect.width / 2}px`;
                                tooltip.style.top = `${rect.top}px`;
                              }
                            }}
                          >
                            {row.totalRevenue > 0 ? (
                              <>
                                <span className="group-hover:underline decoration-dotted">
                                  {row.totalRevenue.toLocaleString(undefined, { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                                </span>
                                {row.revenueBreakdown.length > 0 && (
                                  <div className="revenue-tooltip fixed top-auto left-auto w-72 max-w-screen-90 min-w-56 p-4 bg-popover border border-accent rounded-lg shadow-xl text-sm text-foreground font-sans z-float opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ transform: 'translate(-50%, calc(-100% - 8px))' }}>
                                    <div className="font-semibold text-chart-5 mb-2 pb-2 border-b border-accent text-center">Revenue Breakdown</div>
                                    <div className="text-left space-y-1">
                                      <div><span className="text-muted-foreground">Orders:</span> <span className="text-white font-medium">{row.orderCount}</span></div>
                                      <div className="mt-2">
                                        <span className="text-muted-foreground block mb-1">Per-order revenue:</span>
                                        <ul className="list-disc list-inside text-xs max-h-40 overflow-y-auto space-y-0.5">
                                          {row.revenueBreakdown.map((item, i) => (
                                            <li key={`${item.order_entity_id}-${i}`} className="font-mono truncate" title={item.order_entity_id}>
                                              {item.order_name || item.order_entity_id.slice(-8)} → £{Number(item.revenue).toFixed(2)}{item.imputed && <span className="text-warning"> (imputed)</span>}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    </div>
                                    <div className="pt-2 mt-2 border-t border-accent font-semibold text-chart-5 text-center">
                                      {row.totalRevenue.toLocaleString(undefined, { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td
                            className="px-4 py-3 text-right text-chart-2 font-mono tabular-nums cursor-help group relative overflow-visible"
                            onMouseEnter={(e) => {
                              const tooltip = e.currentTarget.querySelector('.fuel-tooltip') as HTMLElement;
                              if (tooltip) {
                                const rect = e.currentTarget.getBoundingClientRect();
                                tooltip.style.left = `${rect.left + rect.width / 2}px`;
                                tooltip.style.top = `${rect.top}px`;
                              }
                            }}
                          >
                            {row.totalFuelCost > 0 ? (
                              <>
                                <span className="group-hover:underline decoration-dotted">
                                  {row.totalFuelCost.toLocaleString(undefined, { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                                </span>
                                <div className="fuel-tooltip fixed top-auto left-auto w-80 min-w-72 p-4 bg-popover border border-accent rounded-lg shadow-xl text-center text-sm text-foreground font-sans z-float opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ transform: 'translate(-50%, calc(-100% - 8px))' }}>
                                  <div className="font-semibold text-chart-2 mb-2 pb-2 border-b border-accent">Fuel Cost Calculation</div>
                                  <div className="text-left mt-2 mb-2"><span className="text-muted-foreground">Source:</span> {row.totalFuelQuantity > 0 ? 'Observed Jigsaw fuel transactions' : `Estimated: Miles × £/mile (${row.vehicleType})`}</div>
                                  <div className="border-b border-border mb-2" aria-hidden />
                                  {row.totalFuelQuantity > 0 ? (
                                    <>
                                      <div className="text-left mb-1"><span className="text-muted-foreground">Litres:</span> {Number(row.totalFuelQuantity).toFixed(1)}</div>
                                      <div className="text-left mb-1"><span className="text-muted-foreground">Unit price:</span> £{Number(row.fuelUnitPrice).toFixed(3)}/litre</div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="text-left mb-1"><span className="text-muted-foreground">Miles:</span> {Number(row.totalMileage).toFixed(1)}</div>
                                      <div className="text-left mb-1"><span className="text-muted-foreground">Vehicle type:</span> {row.vehicleType}</div>
                                    </>
                                  )}
                                  <div className="pt-2 mt-2 border-t border-accent font-semibold text-chart-2 text-center">
                                    £{Number(row.totalFuelCost).toFixed(2)}
                                  </div>
                                </div>
                              </>
                            ) : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td
                            className="px-4 py-3 text-right text-chart-7 font-mono tabular-nums cursor-help group relative overflow-visible"
                            onMouseEnter={(e) => {
                              const tooltip = e.currentTarget.querySelector('.driver-tooltip') as HTMLElement;
                              if (tooltip) {
                                const rect = e.currentTarget.getBoundingClientRect();
                                tooltip.style.left = `${rect.left + rect.width / 2}px`;
                                tooltip.style.top = `${rect.top}px`;
                              }
                            }}
                          >
                            {row.totalDriverCost > 0 ? (
                              <>
                                <span className="group-hover:underline decoration-dotted">
                                  {row.totalDriverCost.toLocaleString(undefined, { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                                </span>
                                <div className="driver-tooltip fixed top-auto left-auto w-80 min-w-72 p-4 bg-popover border border-accent rounded-lg shadow-xl text-center text-sm text-foreground font-sans z-float opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ transform: 'translate(-50%, calc(-100% - 8px))' }}>
                                  <div className="font-semibold text-chart-7 mb-2 pb-2 border-b border-accent">Driver Cost Calculation</div>
                                  <div className="text-left mt-2 mb-2"><span className="text-muted-foreground">Formula:</span> (Miles × £/mi) + (Driving hrs × £/hr) + (Waiting hrs × £/hr)</div>
                                  <div className="text-left mb-1 text-xs text-muted-foreground">Vehicle type: {row.vehicleType}</div>
                                  <div className="border-b border-border mb-2" aria-hidden />
                                  <div className="text-left mb-1"><span className="text-muted-foreground">I. Miles:</span> {Number(row.totalMileage).toFixed(1)}<span className="text-chart-7"> (mileage component)</span></div>
                                  <div className="text-left mb-1"><span className="text-muted-foreground">II. Driving hrs:</span> {Number(row.totalDrivingHours).toFixed(1)}<span className="text-chart-7"> (driving component)</span></div>
                                  <div className="text-left mb-1"><span className="text-muted-foreground">III. Waiting hrs:</span> {Number(row.totalWaitingHours).toFixed(1)}<span className="text-chart-7"> (waiting component)</span></div>
                                  <div className="pt-2 mt-2 border-t border-accent font-semibold text-chart-7 text-center">
                                    £{Number(row.totalDriverCost).toFixed(2)}
                                  </div>
                                </div>
                              </>
                            ) : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold font-mono tabular-nums">
                            <span className={row.totalProfit >= 0 ? 'text-success' : 'text-destructive'}>
                              {row.totalProfit.toLocaleString(undefined, {
                                style: 'currency',
                                currency: 'GBP',
                                minimumFractionDigits: Math.abs(row.totalProfit) < 1 ? 1 : 0,
                                maximumFractionDigits: Math.abs(row.totalProfit) < 1 ? 1 : 0,
                              })}
                            </span>
                          </td>
                          {showProvenance && (
                            <td className="px-3 py-3 text-center">
                              {fullMatch?.provenance?.profit?.completeness ? (
                                <Badge {...completenessBadge(fullMatch.provenance.profit.completeness)}>
                                  {fullMatch.provenance.profit.completeness}
                                </Badge>
                              ) : (
                                <span className="text-xs text-accent">—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  {/* Totals Footer */}
                  <tfoot className="bg-secondary/50">
                    <tr>
                      <td colSpan={showProvenance ? 9 : 8} className="px-0 py-0">
                        <div className="h-0.5 bg-white shadow-glow-neutral-lg animate-pulse" />
                      </td>
                    </tr>
                    <tr className="text-foreground font-semibold">
                      <td className="px-2 py-3"></td>
                      <td className="px-4 py-3">Total</td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">
                        {kpiTotals.totalMileage.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                      </td>
                      <td className="px-4 py-3 text-right text-chart-5 font-mono tabular-nums">
                        {kpiTotals.totalRevenue.toLocaleString(undefined, { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                      </td>
                      <td className="px-4 py-3 text-right text-chart-2 font-mono tabular-nums">
                        {kpiTotals.totalFuelCost.toLocaleString(undefined, { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                      </td>
                      <td className="px-4 py-3 text-right text-chart-7 font-mono tabular-nums">
                        {kpiTotals.totalDriverCost.toLocaleString(undefined, { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">
                        <span className={kpiTotals.totalProfit >= 0 ? 'text-success' : 'text-destructive'}>
                          {kpiTotals.totalProfit.toLocaleString(undefined, { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                        </span>
                      </td>
                      {showProvenance && <td className="px-3 py-3"></td>}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between py-4 mt-4 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>
                    Showing {(page - 1) * rowsPerPage + 1}–{Math.min(page * rowsPerPage, dailySummaryData.length)} of {dailySummaryData.length} results
                  </span>
                  <span className="text-accent">•</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs">Show</span>
                    <select
                      className="bg-secondary border-accent rounded px-2 py-1 text-xs text-foreground"
                      value={rowsPerPage}
                      onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                    >
                      {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span className="text-xs">per page</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Page {page}/{totalPages}</span>
                  <Button
                    variant="ghost"
                    size="iconSm"
                    className="hover:text-foreground"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <input
                    type="number"
                    min={1}
                    max={totalPages}
                    value={page}
                    onChange={e => {
                      let val = Number(e.target.value);
                      if (val < 1) val = 1;
                      if (val > totalPages) val = totalPages;
                      setPage(val);
                    }}
                    className="w-12 text-center bg-secondary border-accent rounded px-2 py-1 text-sm text-foreground"
                  />
                  <Button
                    variant="ghost"
                    size="iconSm"
                    className="hover:text-foreground"
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      {/* ── Orders Tab ──────────────────────────────────────────────────── */}
      {activeTab === 'orders' && (
        <section className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h4 className="text-sm font-semibold text-white">Merged Orders</h4>
            <span className="text-xs text-muted-foreground">{ordersData.length} orders</span>
          </div>
          {ordersLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-5 h-5 text-success animate-spin" />
              <span className="ml-2 text-muted-foreground text-sm">Loading orders…</span>
            </div>
          ) : ordersData.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No order data available. Click <strong>Import Legacy</strong> to import historical order data.
            </div>
          ) : (
            <div className="overflow-x-auto max-h-150 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-background text-muted-foreground uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Order</th>
                    <th className="px-3 py-2 text-left">Customer</th>
                    <th className="px-3 py-2 text-left">Origin</th>
                    <th className="px-3 py-2 text-left">Destination</th>
                    <th className="px-3 py-2 text-left">Service</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Weight</th>
                    <th className="px-3 py-2 text-left" title="Planned / declared vehicle from the TMS">Vehicle (assigned)</th>
                    <th className="px-3 py-2 text-left">Pick-up vehicle</th>
                    <th className="px-3 py-2 text-left">Delivery vehicle</th>
                    <th className="px-3 py-2 text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {ordersData.slice((ordersPage - 1) * 50, ordersPage * 50).map((row, i) => (
                    <tr key={i} className="hover:bg-secondary/30 transition-colors">
                      <td className="px-3 py-2 text-foreground">{row.date}</td>
                      <td className="px-3 py-2 text-foreground font-mono whitespace-nowrap">{row.order_name ?? row.order_entity_id ?? '—'}</td>
                      <td className="px-3 py-2 text-foreground">{row.customer_name ?? '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground max-w-38 truncate" title={row.origin}>{row.origin ?? '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground max-w-38 truncate" title={row.destination}>{row.destination ?? '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground">{row.service_level ?? '—'}</td>
                      <td className="px-3 py-2 text-foreground text-right">{row.goods_quantity ?? '—'}</td>
                      <td className="px-3 py-2 text-foreground text-right">{row.goods_weight != null ? `${Number(row.goods_weight).toFixed(1)} kg` : '—'}</td>
                      <td className="px-3 py-2 text-foreground">{row.vehicle_assigned?.trim() || '—'}</td>
                      <td className="px-3 py-2 text-foreground">{row.vehicle_pickup?.trim() || '—'}</td>
                      <td className="px-3 py-2 text-foreground">{row.vehicle_observed ?? '—'}</td>
                      <td className="px-3 py-2 text-right">
                        {row.revenue != null ? (
                          <span className={row.revenue_imputed ? 'text-warning' : 'text-success'}>
                            £{Number(row.revenue).toFixed(2)}
                            {row.revenue_imputed && <span className="ml-1 text-xs" title="Imputed">⚠</span>}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {ordersData.length > 50 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    Page {ordersPage}/{Math.ceil(ordersData.length / 50)} ({ordersData.length} total)
                  </span>
                  <div className="flex items-center gap-2">
                    <Button variant="secondary" className="h-auto px-2 py-1 text-xs" disabled={ordersPage === 1} onClick={() => setOrdersPage(p => p - 1)}>Prev</Button>
                    <Button variant="secondary" className="h-auto px-2 py-1 text-xs" disabled={ordersPage >= Math.ceil(ordersData.length / 50)} onClick={() => setOrdersPage(p => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* ── Customer Profitability Tab ───────────────────────────────────── */}
      {activeTab === 'customers' && (
        <section className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h4 className="text-sm font-semibold text-white">Customer Profitability (Daily)</h4>
            <span className="text-xs text-muted-foreground">{customersData.length} records</span>
          </div>
          {customersLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-5 h-5 text-success animate-spin" />
              <span className="ml-2 text-muted-foreground text-sm">Loading customer data…</span>
            </div>
          ) : customersData.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No customer profitability data available. Run <strong>compute:customer-profitability</strong> or import historical data.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-background text-muted-foreground uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Delivery date</th>
                    <th className="px-4 py-3 text-left">Customer</th>
                    <th className="px-4 py-3 text-right">Orders</th>
                    <th className="px-4 py-3 text-right">Revenue</th>
                    <th className="px-4 py-3 text-right">Allocated Cost</th>
                    <th className="px-4 py-3 text-right">Profit</th>
                    <th className="px-4 py-3 text-right">Margin %</th>
                    <th className="px-4 py-3 text-right">Cost/Mile</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {customersData.map((row, i) => (
                    <tr key={i} className="hover:bg-secondary/30 transition-colors">
                      <td className="px-4 py-3 text-foreground">{row.period}</td>
                      <td className="px-4 py-3 text-foreground font-medium">{row.customer_name}</td>
                      <td className="px-4 py-3 text-right text-foreground">{row.order_count}</td>
                      <td className="px-4 py-3 text-right text-success">£{Number(row.revenue ?? 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-warning">£{Number(row.allocated_cost ?? 0).toFixed(2)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${(row.profit ?? 0) >= 0 ? 'text-success' : 'text-destructive'}`}>
                        £{Number(row.profit ?? 0).toFixed(2)}
                      </td>
                      <td className={`px-4 py-3 text-right ${(row.profit_margin ?? 0) >= 0 ? 'text-success' : 'text-destructive'}`}>
                        {Number(row.profit_margin ?? 0).toFixed(1)}%
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {row.cost_per_mile != null ? `£${Number(row.cost_per_mile).toFixed(2)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ============================================================================
// SortableHeader sub-component
// ============================================================================

function SortableHeader({
  field,
  label,
  current,
  asc,
  onSort,
  align = 'left',
  className = '',
}: {
  field: SortField;
  label: string;
  current: SortField;
  asc: boolean;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right';
  className?: string;
}) {
  return (
    <th
      className={`px-4 py-3 cursor-pointer hover:bg-secondary transition-colors ${align === 'right' ? 'text-right' : ''
        } ${className}`}
      onClick={() => onSort(field)}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        {current === field && (
          <span className="text-success text-xs">{asc ? '▲' : '▼'}</span>
        )}
      </div>
    </th>
  );
}
